import { useCallback, useEffect, useMemo } from 'react';
import { Skeleton, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEqual } from 'lodash';
import { useForm } from 'react-hook-form';
import { SwitchElement } from 'react-hook-form-mui';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import * as yup from 'yup';
import {
  AdvancedDesignationsSelector,
  SelectorOption
} from 'components/AdvancedDesignationsSelector';
import { Option } from 'components/ComboCheckBox/ComboCheckBox';
import ComboBoxElement from 'components/ReactHookForm/ComboBoxElement';
import ComboCheckboxElement from 'components/ReactHookForm/ComboCheckBoxElement';
import LimitedTextElement from 'components/ReactHookForm/LimitedTextElement';
import Text from 'components/Text';
import { useAppContext } from 'hooks/useAppContext';
import { useConfigContext } from 'hooks/useConfigContext';
import { useOrgFeatures } from 'hooks/useOrgFeatures';
import { useDesignationGroups, useDesignations } from 'queries/UseDesignations';
import {
  BlockTypes,
  Designation,
  IDesignationsBlock,
  IGivingFormConfig
} from 'types';
import { mapDesignations } from 'utils/designations';
import './EditDesignations.scss';

const designationsSchema = yup
  .object({
    designations: yup.array(),
    designationGroups: yup.array()
  })
  .test(
    'at-least-one-designation',
    'Must select at least one designation or designation group',
    function (value) {
      const { designations = [], designationGroups = [] } = value || {};

      // Count individual designations
      const individualDesignationsCount = designations.length;

      // Count designations within selected groups
      const groupDesignationsCount = designationGroups.reduce(
        (count, group) => count + (group?.designationsInGroup?.length || 0),
        0
      );

      // Must have at least one designation either individual or within groups
      return individualDesignationsCount > 0 || groupDesignationsCount > 0;
    }
  );

const DEFAULT_PROMPT_NAME = 'Select Your Designation';

const mapDesignationToOption = (designation: Designation): SelectorOption => ({
  label: designation.title,
  value: designation.id,
  type: 'individual'
});

const mapOptionToDesignation = (option: Option): Designation => ({
  title: option.label,
  id: option.value as string
});

const mapOptionToId = (option: Option): string => option.value as string;

const isDesignationBlock = (block: IGivingFormConfig['blocks'][number]) =>
  block.blockType === 'DesignationsBlock';

type EditDesignationsFormValues = {
  allowMultipleDesignations: boolean;
  designationsPrompt: string;
  designations: SelectorOption[];
  designationGroups: SelectorOption[];
  defaultDesignation: Designation | undefined;
};

const EditDesignationsForm = ({
  defaultFormValues,
  designations,
  designationGroups,
  onSubmit
}: {
  defaultFormValues: EditDesignationsFormValues;
  designations: Designation[];
  designationGroups: SelectorOption[];
  onSubmit: (values: EditDesignationsFormValues) => void;
}) => {
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { isValid, errors: formErrors }
  } = useForm({
    resolver: yupResolver(designationsSchema),
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    defaultValues: defaultFormValues
  });

  const orgFeatures = useOrgFeatures();

  const watchAllFields = watch();
  const [allFields] = useDebounce(watchAllFields, 500, {
    equalityFn: (left, right) => isEqual(left, right)
  });

  // when debouced form data changes, "submit" it
  useEffect(() => {
    if (isValid) {
      onSubmit(allFields);
    } else {
      console.error('!isValid', allFields, isValid, formErrors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFields, isValid, formErrors]);

  const watchedDesignations = watch('designations');
  const watchedDesignationGroups = watch('designationGroups');

  const selectedDesignationsAndGroups = useMemo(
    () => watchedDesignations.concat(watchedDesignationGroups ?? []),
    [watchedDesignations, watchedDesignationGroups]
  );

  const designationOptions = useMemo(
    () => designations.map(mapDesignationToOption),
    [designations]
  );
  const defaultDesignationOptions = useMemo(() => {
    // Include all individual designations from both default values AND current selection
    const currentIndividualDesignations = watchedDesignations.map(
      mapOptionToDesignation
    );
    const defaultIndividualDesignations = defaultFormValues.designations.map(
      mapOptionToDesignation
    );

    // Include all designations from selected groups from both default values AND current selection
    const groupDesignations: Designation[] = [];

    // Add from current form state
    watchedDesignationGroups?.forEach((group) => {
      if (group.designationsInGroup) {
        group.designationsInGroup.forEach((designation) => {
          groupDesignations.push({
            id: designation.id,
            title: designation.title
          });
        });
      }
    });

    // Add from default form values (important for initial load)
    defaultFormValues.designationGroups?.forEach((group) => {
      if (group.designationsInGroup) {
        group.designationsInGroup.forEach((designation) => {
          groupDesignations.push({
            id: designation.id,
            title: designation.title
          });
        });
      }
    });

    // Combine and deduplicate by id
    const allAvailableDesignations = [
      ...currentIndividualDesignations,
      ...defaultIndividualDesignations,
      ...groupDesignations
    ];
    const uniqueDesignations = allAvailableDesignations.reduce(
      (acc: Designation[], current) => {
        if (!acc.find((d) => d.id === current.id)) {
          acc.push(current);
        }
        return acc;
      },
      []
    );

    return uniqueDesignations;
  }, [
    watchedDesignations,
    watchedDesignationGroups,
    defaultFormValues.designations,
    defaultFormValues.designationGroups
  ]);

  const useAdvancedDesignations = useMemo(
    () => orgFeatures?.advancedDesignations && designationGroups?.length > 0,
    [orgFeatures?.advancedDesignations, designationGroups?.length]
  );

  const handleDesignationsChange = useCallback(
    (options: Option[]) => {
      const newDesignations: Designation[] = options.map(
        mapOptionToDesignation
      );

      const currentValues = getValues();
      if (
        currentValues.defaultDesignation?.id &&
        !newDesignations.find(
          (d) => d.id === currentValues.defaultDesignation?.id
        )
      ) {
        setValue('defaultDesignation', null, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
      }
    },
    [setValue, getValues]
  );

  const handleAdvancedDesignationsChange = useCallback(
    (selected: SelectorOption[]) => {
      const newDesignations = selected.filter((d) => d.type !== 'group');
      const newDesignationGroups = selected.filter((d) => d.type === 'group');

      // Use setValue with shouldValidate and shouldDirty to ensure proper form state update
      setValue('designations', newDesignations, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
      setValue('designationGroups', newDesignationGroups, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });

      // Check if current default designation is still available after this change
      const currentValues = getValues();
      if (currentValues.defaultDesignation?.id) {
        const allAvailableIds = new Set();

        // Add IDs from individual designations
        newDesignations.forEach((d) => allAvailableIds.add(d.value));

        // Add IDs from group designations
        newDesignationGroups.forEach((group) => {
          if (group.designationsInGroup) {
            group.designationsInGroup.forEach((designation) => {
              allAvailableIds.add(designation.id);
            });
          }
        });

        if (!allAvailableIds.has(currentValues.defaultDesignation.id)) {
          setValue('defaultDesignation', null, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
          });
        }
      }
    },
    [setValue, getValues]
  );

  return (
    <Stack spacing={1} className="EditDesignations">
      <Text variant="h3">Prompt Name</Text>
      <LimitedTextElement
        name="designationsPrompt"
        control={control}
        maxChar={50}
        minRows={3}
        multiline
        fullWidth
        placeholder={DEFAULT_PROMPT_NAME}
      />

      <Text
        variant="h3"
        className="EditDesignations__select-designations-header"
      >
        Select Designations
      </Text>
      {useAdvancedDesignations ? (
        <AdvancedDesignationsSelector
          ungroupedDesignations={designationOptions.map((d) => ({
            ...d,
            type: 'individual',
            value: d.value as string
          }))}
          designationGroups={designationGroups}
          onChange={handleAdvancedDesignationsChange}
          defaultValue={selectedDesignationsAndGroups}
        />
      ) : (
        <ComboCheckboxElement
          name="designations"
          control={control}
          options={designationOptions}
          onChange={handleDesignationsChange}
          label={(numSelected) =>
            `(${numSelected}) Designation${numSelected === 1 ? '' : 's'}`
          }
        />
      )}
      <Text
        className="EditDesignations__select-designations-header"
        variant="h3"
      >
        Select Default Designation
      </Text>
      <Text variant="h6Regular">
        If the donor does not select a designation, their donation will go here.
      </Text>
      <ComboBoxElement
        name="defaultDesignation"
        control={control}
        options={defaultDesignationOptions}
        autocompleteProps={{
          className: 'EditDesignations__select-default',
          getOptionLabel: (option) => option.title ?? '',
          isOptionEqualToValue: (option, value) => {
            // Handle null/undefined values
            if (!option || !value) return false;
            return option.id === value.id;
          }
        }}
      />

      <SwitchElement
        control={control}
        className="switch-element EditDesignations__allow-multiple-toggle"
        name="allowMultipleDesignations"
        label="Allow Multiple Designations"
      />
    </Stack>
  );
};

export default (): JSX.Element => {
  const {
    selectedOrganization: { id: organizationId },
    selectedCampaignId
  } = useAppContext();
  const { campaignId: paramCampaignId } = useParams();
  const campaignId = paramCampaignId || selectedCampaignId;

  const { data: designations, isLoading: isLoadingDesignations } =
    useDesignations({
      organizationId,
      campaignId
    });

  const { data: designationGroups, isLoading: isLoadingDesignationGroups } =
    useDesignationGroups({ organizationId, campaignId });

  const isLoading = useMemo(
    () => isLoadingDesignations || isLoadingDesignationGroups,
    [isLoadingDesignations, isLoadingDesignationGroups]
  );

  const mappedDesignationsForCampaign = useMemo(() => {
    if (designations?.length > 0) {
      return designations.map(mapDesignations);
    }
    return [];
  }, [designations]);

  const mappedDesignationGroupsForCampaign: SelectorOption[] = useMemo(
    () =>
      designationGroups
        ? designationGroups.map((d) => ({
            label: d.name,
            value: d.id,
            type: 'group',
            designationsInGroup: d.designations
          }))
        : [],
    [designationGroups]
  );

  const {
    configData: { config },
    updateConfig
  } = useConfigContext<IGivingFormConfig>();

  const designationBlock = useMemo(
    () => config.blocks.find(isDesignationBlock) as IDesignationsBlock,
    [config.blocks]
  );

  const defaultFormValues = useMemo<EditDesignationsFormValues>(() => {
    /// map an array of Designation IDs into an array of SelectorOption[] objects
    const getDesignationObjects = () => {
      if (!designationBlock || designationBlock?.designations?.length === 0) {
        return [];
      }

      let designationsArrayForForm: SelectorOption[] = [];
      if (!designationBlock || designationBlock?.designations?.length === 0)
        return designationsArrayForForm;

      const { designations: designationsFromConfig } = designationBlock;
      if (typeof designationsFromConfig[0] === 'string') {
        designationsArrayForForm = mappedDesignationsForCampaign
          .filter(({ id }) => designationsFromConfig.some((x) => x === id))
          .map(mapDesignationToOption);
      } else {
        designationsArrayForForm = (
          designationsFromConfig as Designation[]
        ).map(mapDesignationToOption);
      }

      return designationsArrayForForm;
    };

    // similarly, map designationGroup IDs into SelectorOptions
    const getDesignationGroupObjects = () => {
      if (
        !designationBlock ||
        designationBlock?.designationGroups?.length === 0
      ) {
        return [];
      }

      const { designationGroups: configuredDesignationGroups } =
        designationBlock;

      // map input values into SelectorOptions -- handle both string[] and DesignationGroup[]
      return mappedDesignationGroupsForCampaign.filter((group) =>
        configuredDesignationGroups?.some((x) =>
          typeof x === 'string' ? x === group.value : x.id === group.value
        )
      );
    };

    const getDefaultDesignationObject = (): Designation | undefined => {
      if (!designationBlock?.defaultDesignation) return undefined;
      if (typeof designationBlock?.defaultDesignation === 'string') {
        // First check individual designations
        const individualDesignation = mappedDesignationsForCampaign.find(
          ({ id }) => id === designationBlock.defaultDesignation
        );
        if (individualDesignation) return individualDesignation;

        // If not found in individual designations, check designations from selected groups
        const selectedGroupIds = getDesignationGroupObjects().map(
          (group) => group.value
        );
        const groupDesignation = mappedDesignationGroupsForCampaign
          .filter(
            (groupOption) =>
              selectedGroupIds.includes(groupOption.value) &&
              groupOption.designationsInGroup
          )
          .flatMap((groupOption) => groupOption.designationsInGroup || [])
          .find(
            (designation) =>
              designation.id === designationBlock.defaultDesignation
          );

        if (groupDesignation) {
          return {
            id: groupDesignation.id,
            title: groupDesignation.title
          };
        }
        return undefined;
      }
      return designationBlock.defaultDesignation as Designation;
    };

    return {
      designationsPrompt: designationBlock?.promptText ?? '',
      designations: getDesignationObjects(),
      designationGroups: getDesignationGroupObjects(),
      defaultDesignation: getDefaultDesignationObject(),
      allowMultipleDesignations: !designationBlock?.singleDesignation ?? true
    };
  }, [
    designationBlock,
    mappedDesignationsForCampaign,
    mappedDesignationGroupsForCampaign
  ]);

  const handleSave = (values: EditDesignationsFormValues) => {
    const commonDesignationBlock: Pick<
      IDesignationsBlock,
      | 'designations'
      | 'promptText'
      | 'defaultDesignation'
      | 'singleDesignation'
      | 'designationGroups'
    > = {
      promptText: values.designationsPrompt ?? DEFAULT_PROMPT_NAME,
      defaultDesignation: values.defaultDesignation?.id || null,
      designations: values.designations
        .filter((d) => d.type !== 'group')
        .map(mapOptionToId),
      designationGroups: values.designationGroups
        .filter((d) => d.type === 'group')
        .map(mapOptionToId),
      singleDesignation: !values.allowMultipleDesignations
    };

    const newConfig = {
      ...config,
      blocks: designationBlock
        ? config.blocks.map((block) =>
            block.blockType === 'DesignationsBlock'
              ? {
                  ...block,
                  ...commonDesignationBlock
                }
              : block
          )
        : [
            ...config.blocks,
            {
              blockType: BlockTypes.DesignationsBlock,
              id: '01-designationsBlock',
              ...commonDesignationBlock
            }
          ]
    };
    updateConfig(newConfig);
  };

  const block = isLoading ? (
    <Stack spacing={1} className="EditDesignations">
      <Text variant="h3">Prompt Name</Text>
      <Skeleton
        animation="wave"
        height={100}
        classes={{
          root: 'EditDesignations__select-designations-loader'
        }}
      />
      <Text
        variant="h3"
        className="EditDesignations__select-designations-header"
      >
        Select Designations
      </Text>
      <Skeleton
        animation="wave"
        height={60}
        classes={{
          root: 'EditDesignations__select-designations-loader'
        }}
      />
      <Text
        className="EditDesignations__select-designations-header"
        variant="h3"
      >
        Select Default Designation
      </Text>
      <Text variant="h6Regular">
        If the donor does not select a designation, their donation will go here.
      </Text>
      <Skeleton
        animation="wave"
        height={60}
        classes={{
          root: 'EditDesignations__select-designations-loader'
        }}
      />
      <Skeleton
        animation="wave"
        height={30}
        classes={{
          root: 'EditDesignations__select-designations-loader'
        }}
      />
    </Stack>
  ) : (
    <EditDesignationsForm
      designations={mappedDesignationsForCampaign ?? []}
      designationGroups={mappedDesignationGroupsForCampaign ?? []}
      defaultFormValues={defaultFormValues}
      onSubmit={handleSave}
    />
  );

  return block;
};
